import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ClipboardList,
  Timer,
  CalendarDays,
  MapPin,
  ChevronRight,
  Users,
  Building2,
  Trophy,
  Calendar,
} from 'lucide-react';
import EventCountdown from '@/components/EventCountdown';
import { useAnnouncements, useStats } from '@/hooks/useApi';
import { useThemeImages } from '@/hooks/useThemeImages';

const HIGHLIGHTS = [
  { key: 'registration', icon: ClipboardList, to: '/announcements', color: 'text-primary' },
  { key: 'liveScores', icon: Timer, to: '/scores', color: 'text-accent' },
  { key: 'scheduleHighlight', icon: CalendarDays, to: '/schedule', color: 'text-secondary' },
  { key: 'venueHighlight', icon: MapPin, to: '/venues', color: 'text-primary-light' },
] as const;

const SCHEDULE_PREVIEW = [
  { day: 1, date: '6/3', titleKey: 'schedule.categories.opening', items: ['開幕典禮', '聖火點燃', '傳統競技初賽'] },
  { day: 2, date: '6/4', titleKey: 'schedule.categories.competition', items: ['各項競賽決賽', '傳統技藝展示', '選手之夜'] },
  { day: 3, date: '6/5', titleKey: 'schedule.categories.closing', items: ['最終決賽', '頒獎典禮', '閉幕典禮'] },
];

export default function HomePage() {
  const { t } = useTranslation();
  const { data: announcementsData } = useAnnouncements(1);
  const { data: statsData } = useStats();
  const themeImages = useThemeImages();

  const latestAnnouncements = Array.isArray(announcementsData?.items) ? announcementsData.items.slice(0, 3) : [];

  const quickStats = [
    { label: t('home.participants'), value: statsData?.totalParticipants ?? '--', icon: Users },
    { label: t('home.townships'), value: statsData?.totalTownships ?? 9, icon: Building2 },
    { label: t('home.competitionCategories'), value: statsData?.totalCompetitions ?? 15, icon: Trophy },
    { label: t('home.eventDays'), value: 3, icon: Calendar },
  ];

  return (
    <div>
      {/* Hero Banner */}
      <section className="hero-gradient relative overflow-hidden">
        <img
          src={themeImages.heroScenes[0]}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-overlay"
        />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-32 text-center">
          <img src={themeImages.logoAlt} alt="Event Logo" className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-6 rounded-full bg-white/10 object-cover drop-shadow-lg" />
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4 drop-shadow-lg">
            {t('home.heroTitle')}
          </h1>
          <p className="text-xl md:text-2xl font-medium mb-2 text-white/90">
            {t('home.heroSubtitle')}
          </p>
          <p className="text-lg md:text-xl mb-3 text-white/80">{t('home.heroDate')}</p>
          <p className="text-base md:text-lg mb-8 text-white/70">{t('home.heroLocation')}</p>

          <EventCountdown />

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/schedule" className="btn-primary text-lg px-8 py-3">
              {t('home.ctaSchedule')}
            </Link>
            <Link to="/scores" className="btn-secondary !border-white !text-white hover:!bg-white/20 text-lg px-8 py-3">
              {t('home.liveScores')}
            </Link>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="section-title text-center">{t('home.highlightsTitle')}</h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {HIGHLIGHTS.map((h) => (
            <Link key={h.key} to={h.to} className="card group hover:scale-[1.02] transition-transform">
              <h.icon className={`w-10 h-10 ${h.color} mb-4`} />
              <h3 className="text-lg font-bold mb-2">{t(`home.${h.key}`)}</h3>
              <p className="text-text-secondary text-sm">{t(`home.${h.key}Desc`)}</p>
              <div className="mt-4 flex items-center text-primary text-sm font-medium group-hover:gap-2 transition-all">
                {t('common.viewDetails')} <ChevronRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-bg-secondary py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="section-title text-center">{t('home.quickStats')}</h2>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {quickStats.map((stat) => (
              <div key={stat.label} className="card text-center">
                <stat.icon className="w-8 h-8 text-primary mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-primary tabular-nums">{stat.value}</div>
                <div className="text-sm text-text-secondary mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Announcements */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="section-title">{t('home.latestAnnouncements')}</h2>
          <Link to="/announcements" className="text-primary font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all">
            {t('home.viewAllAnnouncements')} <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        {latestAnnouncements.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {latestAnnouncements.map((a) => (
              <Link key={a.id} to={`/announcements/${a.id}`} className="card group">
                {a.imageUrl && (
                  <div className="w-full h-40 bg-bg-secondary rounded-md mb-4 overflow-hidden">
                    <img src={a.imageUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </div>
                )}
                <span className="badge text-xs mb-2">{a.category}</span>
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">{a.title}</h3>
                <p className="text-text-secondary text-sm line-clamp-2">{a.excerpt}</p>
                <p className="text-xs text-text-secondary mt-3">{a.createdAt}</p>
              </Link>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card">
                <div className="w-full h-40 bg-bg-secondary rounded-md mb-4 animate-pulse" />
                <div className="h-4 bg-bg-secondary rounded w-1/4 mb-3 animate-pulse" />
                <div className="h-5 bg-bg-secondary rounded w-3/4 mb-2 animate-pulse" />
                <div className="h-4 bg-bg-secondary rounded w-full animate-pulse" />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Schedule Preview */}
      <section className="bg-bg-secondary py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="section-title text-center">{t('home.schedulePreview')}</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {SCHEDULE_PREVIEW.map((day) => (
              <div key={day.day} className="card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg">
                    {day.day}
                  </div>
                  <div>
                    <div className="font-bold">{t(`schedule.day${day.day}` as 'schedule.day1')}</div>
                    <div className="text-sm text-text-secondary">{day.date}</div>
                  </div>
                </div>
                <ul className="space-y-2">
                  {day.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-text-secondary">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/schedule" className="btn-primary">{t('home.ctaSchedule')}</Link>
          </div>
        </div>
      </section>

      {/* Cultural Scenes Gallery */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="section-title text-center">{t('home.culturalGallery', '文化風采')}</h2>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {themeImages.scenes.map((src, i) => (
            <div key={i} className="rounded-lg overflow-hidden shadow-md aspect-square bg-bg-secondary">
              <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-center gap-4 flex-wrap">
          {themeImages.characters.slice(0, 4).map((src, i) => (
            <img key={i} src={src} alt="" className="w-20 h-20 md:w-24 md:h-24 object-contain" />
          ))}
        </div>
      </section>

      {/* Organizers */}
      <section className="max-w-7xl mx-auto px-4 py-16 text-center">
        <h2 className="section-title">{t('home.organizers')}</h2>
        <p className="text-text-secondary mt-4">{t('footer.organizer')}</p>
      </section>
    </div>
  );
}
